/* ==========================================================================
   ModaWist — Homepage behaviour
   Hero: respect prefers-reduced-motion (hide video, keep poster) and pause
   off-screen video to protect Core Web Vitals. No dependencies.
   ========================================================================== */
(function () {
  'use strict';

  function initHero(hero) {
    if (hero.dataset.mwHeroInit) return;
    hero.dataset.mwHeroInit = 'true';

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var videos = hero.querySelectorAll('[data-mw-hero-video] video');

    if (reduce) {
      hero.querySelectorAll('[data-mw-hero-video]').forEach(function (wrap) {
        wrap.remove();
      });
      return;
    }

    // Pause when scrolled out of view; resume when back.
    if ('IntersectionObserver' in window && videos.length) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var v = entry.target;
            if (entry.isIntersecting) {
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.1 }
      );
      videos.forEach(function (v) {
        io.observe(v);
      });
    }
  }

  /* ── New Arrivals — continuous auto-scroll (seamless marquee) ───────── */
  function initAutoScroll(track) {
    if (track.dataset.mwScrollInit) return;
    track.dataset.mwScrollInit = 'true';
    if (track.getAttribute('data-mw-autoscroll') === 'false') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;

    // Clone the items once so the loop is seamless.
    originals.forEach(function (node) {
      var clone = node.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });

    // Continuous scroll fights scroll-snap, so disable snapping here.
    track.style.scrollSnapType = 'none';

    var speed = 0.75; // px per frame (~45px/s)
    var paused = false;
    var resumeTimer = null;
    // iOS floors scrollLeft on read, so keep our own float accumulator.
    var pos = track.scrollLeft || 0;
    var isDown = false, startX = 0, startScroll = 0, moved = false;

    function pauseFor(ms) {
      paused = true;
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; }, ms || 1600);
    }
    function syncPos() { pos = track.scrollLeft; }

    track.addEventListener('mouseenter', function () { paused = true; });
    track.addEventListener('mouseleave', function () { if (!isDown) paused = false; });
    track.addEventListener('touchstart', function () { paused = true; }, { passive: true });
    track.addEventListener('touchend', function () { syncPos(); pauseFor(1600); }, { passive: true });
    track.addEventListener('wheel', function () { syncPos(); pauseFor(1600); }, { passive: true });
    track.addEventListener('scroll', function () { if (paused && !isDown) pos = track.scrollLeft; }, { passive: true });

    // Drag-to-scroll with the mouse (touch already scrolls natively).
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      isDown = true;
      moved = false;
      paused = true;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', function (e) {
      if (!isDown) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startScroll - dx;
    });
    function endDrag() {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      syncPos();
      pauseFor(1500);
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });
    // Suppress the click that follows a drag so tiles don't navigate.
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    function tick() {
      if (!paused && !document.hidden) {
        var half = track.scrollWidth / 2;
        pos += speed;
        if (half > 0 && pos >= half) pos -= half;
        track.scrollLeft = pos;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  /* ── Hero Carousel — auto-advancing fade slideshow ─────────────────── */
  function initHeroCarousel(root) {
    if (root.dataset.mwHerocInit) return;
    root.dataset.mwHerocInit = 'true';

    var slides = Array.prototype.slice.call(root.querySelectorAll('.mw-heroc__slide'));
    if (!slides.length) return;
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-heroc-dots] .mw-heroc__dot'));
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var current = 0;
    var timer = null;
    var autoplay = root.getAttribute('data-autoplay') !== 'false' && !reduce && slides.length > 1;
    var speed = (parseInt(root.getAttribute('data-speed'), 10) || 6) * 1000;

    function playVideo(slide, play) {
      var v = slide.querySelector('[data-heroc-video] video');
      if (!v) return;
      if (play && !reduce) {
        var p = v.play();
        if (p && p.catch) p.catch(function () {});
      } else {
        v.pause();
      }
    }

    function goTo(i) {
      i = (i + slides.length) % slides.length;
      slides.forEach(function (s, idx) {
        var active = idx === i;
        s.classList.toggle('is-active', active);
        playVideo(s, active);
      });
      dots.forEach(function (d, idx) { d.classList.toggle('is-active', idx === i); });
      current = i;
    }

    function start() { stop(); if (autoplay) timer = setInterval(function () { goTo(current + 1); }, speed); }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    var prevBtn = root.querySelector('[data-heroc-prev]');
    var nextBtn = root.querySelector('[data-heroc-next]');
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); start(); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () { goTo(parseInt(d.getAttribute('data-index'), 10)); start(); });
    });
    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);

    goTo(0);
    start();
  }

  /* ── Scroll reveal (staggered entrances) ───────────────────────────── */
  function initReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-mw-reveal]'));
    if (!els.length) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (e) { e.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add('is-revealed');
            io.unobserve(en.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    els.forEach(function (e) {
      if (e.dataset.mwRevealObserved) return;
      e.dataset.mwRevealObserved = 'true';
      io.observe(e);
    });
  }

  function init() {
    document.querySelectorAll('[data-mw-hero]').forEach(initHero);
    document.querySelectorAll('[data-mw-autoscroll]').forEach(initAutoScroll);
    document.querySelectorAll('[data-mw-heroc]').forEach(initHeroCarousel);
    initReveal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
